import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Compass, Sun, CalendarRange, Plane, Landmark, Users, RefreshCw, TrendingUp, MapPin } from "lucide-react";

const GEN_COLOR = {
  tourism: "#0ea5e9", business: "#6366f1", mare: "#06b6d4", universita: "#f59e0b",
  ospedali: "#ef4444", fiere_congressi: "#ec4899", crociere: "#14b8a6", eventi_ricorrenti: "#a855f7",
};

export default function DestinationAnalysis() {
  const { t, lang, properties, activeId, selectProperty } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const active = properties.find((p) => p.id === activeId) || properties[0];

  const MONTHS = lang === "it"
    ? ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"]
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const load = useCallback(async (id, refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${id}/destination-analysis?lang=${lang}${refresh ? "&refresh=true" : ""}`);
      setData(data);
    } catch { toast.error("Error"); } finally { setLoading(false); }
  }, [lang]);

  useEffect(() => { if (active) load(active.id); }, [active, load]);

  if (!properties.length) {
    return (
      <div className="App min-h-screen"><Nav />
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
          <Compass className="w-10 h-10 text-primary mx-auto mb-5" />
          <h1 className="text-3xl font-bold mb-3">{t("noProps")}</h1>
          <Button data-testid="da-add-btn" size="lg" onClick={() => nav("/properties/new")}>{t("addProperty")}</Button>
        </div>
      </div>
    );
  }

  const gens = data?.demand_generators || [];
  const season = data?.seasonality || {};
  const monthChip = (m, on) => (
    <span key={m} className={`text-xs font-data px-2 py-1 rounded ${on ? "text-white" : "bg-secondary text-muted-foreground"}`}
      style={on ? { backgroundColor: "var(--primary)" } : undefined}>{MONTHS[(m - 1) % 12]}</span>
  );

  const Section = ({ icon: Icon, title, children, testid }) => (
    <div data-testid={testid} className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3 text-muted-foreground">
        <Icon className="w-4 h-4" /><span className="text-[11px] uppercase tracking-widest font-bold">{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Compass className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold leading-tight">{t("destinationAnalysis")}</h1>
              <p className="text-sm text-muted-foreground">{t("destinationSubtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={active.id} onValueChange={selectProperty}>
              <SelectTrigger data-testid="da-property-switcher" className="w-56 h-10 font-semibold"><SelectValue /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="da-regenerate" variant="outline" size="sm" onClick={() => load(active.id, true)} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />{t("regenerate")}
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground py-24 text-center flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 animate-pulse text-primary" /> {t("analyzing")}
          </p>
        ) : !data ? <p className="text-muted-foreground py-20 text-center">—</p> : (
          <div className="space-y-6">
            {/* summary */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-card border-2 border-primary/20 rounded-lg p-6" data-testid="da-summary">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="text-lg font-semibold flex items-center gap-1">{data.city || t("destinationAnalysis")}</span>
                {data._ai && <span className="ml-auto text-[10px] uppercase tracking-wide font-bold text-primary/70">{t("aiGenerated")}</span>}
              </div>
              <p className="text-sm leading-relaxed">{data.summary}</p>
            </motion.div>

            {/* demand generators */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-card border border-border rounded-lg p-6" data-testid="da-generators">
              <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                <TrendingUp className="w-4 h-4" /><span className="text-[11px] uppercase tracking-widest font-bold">{t("demandGenerators")}</span>
              </div>
              <p className="text-xs text-muted-foreground -mt-2 mb-4">{lang === "it" ? "Punteggio di intensità della vocazione (0–100), non una percentuale. Indica quanto è forte quella componente nella zona." : "Vocation intensity score (0–100), not a percentage. It shows how strong that component is in the area."}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {gens.map((g) => {
                  const color = GEN_COLOR[g.key] || "var(--primary)";
                  return (
                    <div key={g.key} data-testid={`da-gen-${g.key}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold">{g.label}</span>
                        <span className="font-data font-bold" style={{ color }}>{g.score}<span className="text-muted-foreground font-normal">/100</span></span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-1.5">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${g.score}%` }} transition={{ duration: 0.6 }}
                          className="h-full rounded-full" style={{ backgroundColor: color }} />
                      </div>
                      <p className="text-xs text-muted-foreground leading-snug">{g.reason}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section icon={Sun} title={t("seasonalityTitle")} testid="da-seasonality">
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1.5">{t("peakMonths")}</p>
                  <div className="flex flex-wrap gap-1.5">{(season.peak_months || []).map((m) => monthChip(m, true))}</div>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1.5">{t("lowMonths")}</p>
                  <div className="flex flex-wrap gap-1.5">{(season.low_months || []).map((m) => monthChip(m, false))}</div>
                </div>
                {season.note && <p className="text-xs text-muted-foreground leading-relaxed">{season.note}</p>}
              </Section>

              <Section icon={Users} title={t("travelerProfile")} testid="da-traveler">
                <p className="text-sm leading-relaxed">{data.traveler_profile}</p>
              </Section>

              <Section icon={Plane} title={t("referenceAirports")} testid="da-airports">
                <ul className="space-y-1.5">
                  {(data.reference_airports || []).map((a, i) => (
                    <li key={i} className="text-sm flex items-start gap-2"><Plane className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />{a}</li>
                  ))}
                  {!(data.reference_airports || []).length && <li className="text-sm text-muted-foreground">—</li>}
                </ul>
              </Section>

              <Section icon={CalendarRange} title={t("recurringEvents")} testid="da-events">
                <div className="flex flex-wrap gap-2">
                  {(data.recurring_events || []).map((e, i) => (
                    <span key={i} className="text-xs bg-accent text-accent-foreground rounded-full px-3 py-1">{e}</span>
                  ))}
                  {!(data.recurring_events || []).length && <span className="text-sm text-muted-foreground">—</span>}
                </div>
              </Section>

              <div className="md:col-span-2">
                <Section icon={Landmark} title={t("mainAttractions")} testid="da-attractions">
                  <div className="flex flex-wrap gap-2">
                    {(data.main_attractions || []).map((a, i) => (
                      <span key={i} className="text-sm bg-secondary rounded-md px-3 py-1.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-primary" />{a}</span>
                    ))}
                    {!(data.main_attractions || []).length && <span className="text-sm text-muted-foreground">—</span>}
                  </div>
                </Section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
